import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Collapse from 'react-bootstrap/Collapse';
import Spinner from 'react-bootstrap/Spinner';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import {
  TECH_DECISIONS,
  TECH_DEFAULT_RISK_LEVEL,
  TECH_RISK_LEVELS,
  buildTechnicalReviewPayload,
  formatCandidateOcLabel,
  getOcMatchTypeLabel,
  getOcTechDecisionLabel,
  getTechnicalReviewVisibleFields,
  hasTechnicalReviewDecision,
  techDecisionNeedsRejection,
} from '../../utils/ocRequest';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import './OcRequestStyle.css';

const LOG_CONTEXT = 'OcTechnicalReviewForm';

const EMPTY = {
  storage_location: '',
  available_vials: '',
  current_passage: '',
  growth_quality: '',
  known_difficulties: '',
  contamination_history: '',
  expected_recovery_time: '',
  medium_availability: '',
  required_expansion_time: '',
  minimum_quantity_supplyable: '',
  recommended_shipping_format: '',
  risk_level: TECH_DEFAULT_RISK_LEVEL,
  estimated_preparation_days: '',
  decision: '',
  rejection_reason: '',
  comments: '',
};

const FIELD_META = {
  storage_location: { label: 'Storage location', kind: 'text', col: 6 },
  current_passage: { label: 'Current passage', kind: 'text', col: 6 },
  growth_quality: { label: 'Growth quality', kind: 'text', col: 6 },
  recommended_shipping_format: {
    label: 'Recommended shipping format',
    kind: 'text',
    col: 6,
  },
  risk_level: { label: 'Risk level', kind: 'risk', col: 6 },
  available_vials: { label: 'Available vials', kind: 'number', col: 6 },
  estimated_preparation_days: {
    label: 'Estimated preparation days',
    kind: 'number',
    col: 6,
  },
  known_difficulties: { label: 'Known difficulties', kind: 'textarea', col: 12 },
  comments: { label: 'Comments', kind: 'textarea', col: 12 },
  rejection_reason: { label: 'Rejection reason', kind: 'rejection', col: 12 },
};

function decisionBadgeBg(decision) {
  if (decision === 'approved' || decision === 'approved_with_conditions') {
    return 'success';
  }
  if (techDecisionNeedsRejection(decision)) return 'danger';
  return 'warning';
}

/**
 * Per-candidate technical review editor (no per-row save).
 * Collapsed card until expanded; Decision first, then only fields for that decision.
 * Parent submits all reviews via ref.validate() / getPayload().
 */
const OcTechnicalReviewForm = forwardRef(function OcTechnicalReviewForm(
  { api, candidate, expanded = false, onToggle, readOnly = false },
  ref
) {
  const [values, setValues] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!api) return undefined;
    setLoading(true);
    api.getTechnicalReview(candidate.id).then(result => {
      if (cancelled) return;
      setLoading(false);
      if (result.success) {
        const loaded = { ...EMPTY, ...result.data };
        const riskOk = TECH_RISK_LEVELS.some(o => o.value === loaded.risk_level);
        if (!riskOk) loaded.risk_level = TECH_DEFAULT_RISK_LEVEL;
        if (!hasTechnicalReviewDecision(loaded.decision)) loaded.decision = '';
        setValues(loaded);
        logInfo(LOG_CONTEXT, 'tech review loaded', {
          candidateId: candidate.id,
          mode: api.mode,
          decision: loaded.decision,
        });
      } else if (result.status !== 404) {
        showToast(result.messages?.[0] || 'Failed to load review', 'error', 'top-right', 4000);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [api, candidate.id]);

  const setField = (key, value) => setValues(prev => ({ ...prev, [key]: value }));

  const needsRejection = techDecisionNeedsRejection(values.decision);
  const visibleFields = getTechnicalReviewVisibleFields(values.decision);

  useImperativeHandle(
    ref,
    () => ({
      candidateId: candidate.id,
      validate() {
        const ocName =
          candidate.display_oc_id || candidate.free_text_oc_id || `#${candidate.id}`;
        if (!hasTechnicalReviewDecision(values.decision)) {
          return {
            ok: false,
            message: `Choose a decision for ${ocName}`,
          };
        }
        if (needsRejection && !String(values.rejection_reason || '').trim()) {
          return {
            ok: false,
            message: `Rejection reason required for ${ocName}`,
          };
        }
        return { ok: true };
      },
      getPayload() {
        return {
          candidate_id: candidate.id,
          ...buildTechnicalReviewPayload(values),
        };
      },
    }),
    [candidate, needsRejection, values]
  );

  if (loading) {
    return (
      <Card className="oc-glass-panel oc-tech-review-card mb-3 border-0">
        <Card.Body className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" />
          <span className="text-muted">
            Loading review for {formatCandidateOcLabel(candidate)}…
          </span>
        </Card.Body>
      </Card>
    );
  }

  const hasDecision = hasTechnicalReviewDecision(values.decision);
  const decisionLabel = hasDecision
    ? getOcTechDecisionLabel(values.decision)
    : null;
  const ocLabel = formatCandidateOcLabel(candidate);
  const matchLabel = getOcMatchTypeLabel(candidate.match_type) || null;
  const incompleteRejection =
    needsRejection && !String(values.rejection_reason || '').trim();

  const renderField = key => {
    const meta = FIELD_META[key];
    if (!meta) return null;
    const requiredMark = key === 'rejection_reason' && needsRejection ? ' *' : '';
    let control;
    if (meta.kind === 'risk') {
      control = (
        <Form.Select
          value={values.risk_level || TECH_DEFAULT_RISK_LEVEL}
          onChange={e => setField('risk_level', e.target.value)}
        >
          {TECH_RISK_LEVELS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      );
    } else if (meta.kind === 'number') {
      control = (
        <Form.Control
          type="number"
          value={values[key] ?? ''}
          onChange={e => setField(key, e.target.value)}
        />
      );
    } else if (meta.kind === 'textarea') {
      control = (
        <Form.Control
          as="textarea"
          rows={2}
          value={values[key] || ''}
          onChange={e => setField(key, e.target.value)}
        />
      );
    } else {
      control = (
        <Form.Control
          value={values[key] || ''}
          onChange={e => setField(key, e.target.value)}
          required={key === 'rejection_reason' && needsRejection}
        />
      );
    }
    return (
      <Col md={meta.col} key={key}>
        <Form.Group>
          <Form.Label>
            {meta.label}
            {requiredMark}
          </Form.Label>
          {control}
        </Form.Group>
      </Col>
    );
  };

  return (
    <Card
      className={`oc-glass-panel oc-tech-review-card mb-3 border-0${
        expanded ? ' is-expanded' : ''
      }`}
    >
      <Card.Header
        className="oc-glass-header d-flex flex-wrap align-items-center gap-2 oc-tech-review-card__header"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => onToggle?.(candidate.id)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.(candidate.id);
          }
        }}
      >
        <div className="me-auto">
          <div className="fw-semibold d-flex align-items-center gap-2">
            {expanded ? (
              <ChevronUp aria-hidden size={18} />
            ) : (
              <ChevronDown aria-hidden size={18} />
            )}
            Organoid {ocLabel}
          </div>
          <div className="small fw-normal text-muted ps-4">
            {matchLabel ? `Match: ${matchLabel}` : 'Candidate for technical review'}
            {incompleteRejection ? ' · Add a rejection reason' : ''}
          </div>
        </div>
        {hasDecision ? (
          <Badge bg={decisionBadgeBg(values.decision)}>{decisionLabel}</Badge>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant={expanded ? 'outline-secondary' : 'primary'}
          className={`d-inline-flex align-items-center gap-1${
            expanded ? '' : ' oc-primary-btn'
          }`}
          onClick={e => {
            e.stopPropagation();
            onToggle?.(candidate.id);
          }}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
          {expanded ? 'Hide details' : 'Review this OC'}
        </Button>
      </Card.Header>
      <Collapse in={expanded} unmountOnExit={false}>
        <div>
          <Card.Body>
            <fieldset disabled={readOnly} className="border-0 p-0 m-0">
            <Row className="g-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Decision</Form.Label>
                  <Form.Select
                    value={values.decision || ''}
                    onChange={e => setField('decision', e.target.value)}
                  >
                    <option value="">Select a decision…</option>
                    {TECH_DECISIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {visibleFields.map(renderField)}
            </Row>
            {incompleteRejection ? (
              <Alert variant="warning" className="mt-3 py-2 mb-0">
                Rejection reason is required for this decision.
              </Alert>
            ) : null}
            </fieldset>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
});

export default OcTechnicalReviewForm;
